import { Injectable } from '@angular/core';

import { EspecificacaoCompra } from 'src/app/core/models/especificacao-compra.model';
import { ItemIntencaoDeCompra } from 'src/app/core/models/item-intencao-compra.model';
import { AppLocalStorageService } from 'src/app/shared/services/app-local-storage.service';
import { NormalizerHelper } from 'src/app/core/helpers/normalizer.helper';

@Injectable({
  providedIn: 'root',
})
export class RascunhoItensCompraService {
  static readonly STORAGE_KEY = 'rascunhos-itens-compra';
  static readonly LEGACY_STORAGE_KEY = 'rascunhos-itens-compra:v1';

  constructor(private localStorageService: AppLocalStorageService) {}

  atualizarSolicitacao(codUsuario: number, codSolicitacao: number, originais: EspecificacaoCompra[], selecionados: EspecificacaoCompra[]): void {
    if (!this.identificadorValido(codUsuario) || !this.identificadorValido(codSolicitacao)) return;

    const rascunho = this.criarDelta(originais, selecionados);
    const chave = this.chaveSolicitacao(codUsuario, codSolicitacao);
    const dados = this.lerDados();
    if (!rascunho) {
      delete dados.solicitacoes[chave];
      this.gravarDados(dados);
      return;
    }

    dados.solicitacoes[chave] = rascunho;
    this.gravarDados(dados);
  }

  aplicarSolicitacao(codUsuario: number, codSolicitacao: number, itensPersistidos: EspecificacaoCompra[]): EspecificacaoCompra[] {
    const rascunho = this.lerDados().solicitacoes[this.chaveSolicitacao(codUsuario, codSolicitacao)];
    if (!rascunho) return this.copiar(itensPersistidos);

    const removidos = new Set(rascunho.removidas);
    const editadas = new Map(rascunho.editadas.map(item => [item.CODESPECIFICACAOCOMPRA, item]));
    const itens = this.copiar(itensPersistidos)
      .filter(item => !removidos.has(item.CODESPECIFICACAOCOMPRA))
      .map(item => {
        const edicao = editadas.get(item.CODESPECIFICACAOCOMPRA);
        return edicao ? { ...item, QTD: edicao.QTD, PRIORIDADE: edicao.PRIORIDADE, VALOR: edicao.VALOR } : item;
      });

    const codigosExistentes = new Set(itens.map(item => item.ESPECIFICACAO?.CODESPECIFICACAO));
    rascunho.adicionadas.forEach(item => {
      if (!codigosExistentes.has(item.ESPECIFICACAO?.CODESPECIFICACAO)) itens.push(this.copiar(item));
    });
    return itens;
  }

  removerSolicitacao(codUsuario: number, codSolicitacao: number): void {
    const dados = this.lerDados();
    delete dados.solicitacoes[this.chaveSolicitacao(codUsuario, codSolicitacao)];
    this.gravarDados(dados);
  }

  possuiAlteracoesSolicitacao(codUsuario: number, codSolicitacao: number): boolean {
    if (!this.identificadorValido(codUsuario) || !this.identificadorValido(codSolicitacao)) return false;
    return !!this.lerDados().solicitacoes[this.chaveSolicitacao(codUsuario, codSolicitacao)];
  }

  atualizarProvisorio(codUsuario: number, ano: number, itens: ItemSelecionado[], codUnidade: number): void {
    if (!this.identificadorValido(codUsuario) || !this.identificadorValido(ano)) return;
    const dados = this.lerDados();
    const chave = this.chaveProvisoria(codUsuario, ano, codUnidade);
    if (!itens.length) {
      delete dados.provisorios[chave];
    } else {
      dados.provisorios[chave] = this.copiar(itens.map(item => ({ ...item,
        value: { ...item.value, VALORMEDIOUNITARIO: NormalizerHelper.arredondarMoeda(item.value.VALORMEDIOUNITARIO) },
      })));
    }
    this.gravarDados(dados);
  }

  obterProvisorio(codUsuario: number, ano: number, codUnidade: number): ItemSelecionado[] {
    return this.copiar(this.lerDados().provisorios[this.chaveProvisoria(codUsuario, ano, codUnidade)] ?? []);
  }

  removerProvisorio(codUsuario: number, ano: number, codUnidade: number): void {
    const dados = this.lerDados();
    delete dados.provisorios[this.chaveProvisoria(codUsuario, ano, codUnidade)];
    this.gravarDados(dados);
  }

  possuiCriacaoIndeterminada(codUsuario: number, ano: number, codUnidade: number): boolean {
    return !!this.lerDados().criacoesIndeterminadas[this.chaveProvisoria(codUsuario, ano, codUnidade)];
  }

  marcarCriacaoIndeterminada(codUsuario: number, ano: number, codUnidade: number): boolean {
    const dados = this.lerDados();
    const chave = this.chaveProvisoria(codUsuario, ano, codUnidade);
    dados.criacoesIndeterminadas[chave] = new Date().toISOString();
    this.gravarDados(dados);
    return this.possuiCriacaoIndeterminada(codUsuario, ano, codUnidade);
  }

  resolverCriacaoIndeterminada(codUsuario: number, ano: number, codUnidade: number): void {
    const dados = this.lerDados();
    delete dados.criacoesIndeterminadas[this.chaveProvisoria(codUsuario, ano, codUnidade)];
    this.gravarDados(dados);
  }

  migrarProvisorio(codUsuario: number, ano: number, codSolicitacao: number, codSolicitacaoCompra: number, codUnidade: number): void {
    const dados = this.lerDados();
    const chaveProvisoria = this.chaveProvisoria(codUsuario, ano, codUnidade);
    const itens = dados.provisorios[chaveProvisoria];
    if (!itens?.length || !this.identificadorValido(codSolicitacao) || !this.identificadorValido(codSolicitacaoCompra)) return;

    const chaveSolicitacao = this.chaveSolicitacao(codUsuario, codSolicitacao);
    dados.solicitacoes[chaveSolicitacao] = {
      adicionadas: itens.map(item => ({
        CODSOLICITACAOCOMPRA: codSolicitacaoCompra,
        ESPECIFICACAO: item.value.ESPECIFICACAO,
        QTD: item.quantidade,
        PRIORIDADE: item.value.PRIORIDADEMEDIA.CODPRIORIDADE,
        VALOR: NormalizerHelper.arredondarMoeda(item.value.VALORMEDIOUNITARIO),
      } as EspecificacaoCompra)),
      editadas: [],
      removidas: [],
    };
    delete dados.provisorios[chaveProvisoria];
    this.gravarDados(dados);
  }

  possuiProvisorioLegado(codUsuario: number, ano: number): boolean {
    return !!this.lerDados().provisorios[`${codUsuario}:COMPRA:${ano}`]?.length;
  }

  private criarDelta(originais: EspecificacaoCompra[], selecionados: EspecificacaoCompra[]): RascunhoSolicitacao | null {
    const adicionadas = selecionados.filter(item => !originais.some(original => original.ESPECIFICACAO?.CODESPECIFICACAO === item.ESPECIFICACAO?.CODESPECIFICACAO));
    const removidas = originais
      .filter(item => !selecionados.some(selecionado => selecionado.ESPECIFICACAO?.CODESPECIFICACAO === item.ESPECIFICACAO?.CODESPECIFICACAO))
      .map(item => item.CODESPECIFICACAOCOMPRA)
      .filter((codigo): codigo is number => this.identificadorValido(codigo));
    const editadas = selecionados
      .map(item => {
        const original = originais.find(itemOriginal => itemOriginal.ESPECIFICACAO?.CODESPECIFICACAO === item.ESPECIFICACAO?.CODESPECIFICACAO);
        if (!original || original.QTD === item.QTD && original.PRIORIDADE === item.PRIORIDADE && original.VALOR === item.VALOR) return null;
        return { CODESPECIFICACAOCOMPRA: item.CODESPECIFICACAOCOMPRA, QTD: item.QTD, PRIORIDADE: item.PRIORIDADE, VALOR: item.VALOR };
      })
      .filter((item): item is EdicaoRascunho => !!item && this.identificadorValido(item.CODESPECIFICACAOCOMPRA));
    return adicionadas.length || removidas.length || editadas.length ? { adicionadas: this.copiar(adicionadas), editadas, removidas } : null;
  }

  private lerDados(): DadosRascunho {
    try {
      const dadosAtuais = this.lerDadosDaChave(RascunhoItensCompraService.STORAGE_KEY);
      if (dadosAtuais) return dadosAtuais;

      const dadosLegados = this.lerDadosDaChave(RascunhoItensCompraService.LEGACY_STORAGE_KEY);
      if (!dadosLegados) return this.criarDadosVazios();

      this.migrarDadosLegados(dadosLegados);
      return dadosLegados;
    } catch {
      return this.criarDadosVazios();
    }
  }

  private lerDadosDaChave(chave: string): DadosRascunho | null {
    const valor = this.localStorageService.getItem(chave);
    const dados = valor ? JSON.parse(valor) as Partial<DadosRascunho> : null;
    if (!dados?.solicitacoes || !dados.provisorios) return null;
    return { solicitacoes: dados.solicitacoes, provisorios: dados.provisorios, criacoesIndeterminadas: dados.criacoesIndeterminadas ?? {} };
  }

  private migrarDadosLegados(dados: DadosRascunho): void {
    try {
      this.localStorageService.setItem(RascunhoItensCompraService.STORAGE_KEY, dados);
      this.localStorageService.removeItem(RascunhoItensCompraService.LEGACY_STORAGE_KEY);
    } catch {}
  }

  private criarDadosVazios(): DadosRascunho {
    return { solicitacoes: {}, provisorios: {}, criacoesIndeterminadas: {} };
  }

  private gravarDados(dados: DadosRascunho): void {
    try {
      if (!Object.keys(dados.solicitacoes).length && !Object.keys(dados.provisorios).length && !Object.keys(dados.criacoesIndeterminadas).length) {
        this.localStorageService.removeItem(RascunhoItensCompraService.STORAGE_KEY);
        return;
      }
      this.localStorageService.setItem(RascunhoItensCompraService.STORAGE_KEY, dados);
    } catch {}
  }

  private chaveSolicitacao(codUsuario: number, codSolicitacao: number): string {
    return `${codUsuario}:${codSolicitacao}`;
  }

  private chaveProvisoria(codUsuario: number, ano: number, codUnidade: number): string {
    if (!this.identificadorValido(codUsuario) || !this.identificadorValido(ano) || !this.identificadorValido(codUnidade)) throw new Error('Contexto do rascunho de Compra inválido.');
    return `${codUsuario}:COMPRA:${codUnidade}:${ano}`;
  }

  private identificadorValido(valor: number | null | undefined): valor is number {
    return Number.isSafeInteger(valor) && valor > 0;
  }

  private copiar<T>(valor: T): T {
    return JSON.parse(JSON.stringify(valor)) as T;
  }
}

export interface ItemSelecionado {
  value: ItemIntencaoDeCompra;
  quantidade: number;
}

interface DadosRascunho {
  solicitacoes: Record<string, RascunhoSolicitacao>;
  provisorios: Record<string, ItemSelecionado[]>;
  criacoesIndeterminadas: Record<string, string>;
}

interface RascunhoSolicitacao {
  adicionadas: EspecificacaoCompra[];
  editadas: EdicaoRascunho[];
  removidas: number[];
}

interface EdicaoRascunho {
  CODESPECIFICACAOCOMPRA: number;
  QTD: number;
  PRIORIDADE: number;
  VALOR: number;
}
